import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { generateCodeChallenge, generateCodeVerifier, generateState } from '../_utils/helper'
import { useServiceOAuthRedirectUri } from './use-service-oauth-redirect-uri'

// Session storage keys for OAuth state persistence
export const OAUTH_SESSION_STORAGE_KEYS = {
  OAUTH_STATE: 'oauth_state',
  OAUTH_VERIFIER: 'oauth_verifier',
  OAUTH_SERVER_URL: 'oauth_server_url',
  OAUTH_METADATA: 'oauth_metadata',
  OAUTH_CLIENT_REGISTRATION: 'oauth_client_registration',
}

export const SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY = 'service_registry_form_data'

// OAuth server metadata from discovery endpoint
interface ServerMetadata {
  authorization_endpoint: string
  token_endpoint: string
  registration_endpoint: string
  scopes_supported?: string[]
  response_types_supported?: string[]
  grant_types_supported?: string[]
  code_challenge_methods_supported?: string[]
}

// Client registration response
interface ClientRegistration {
  client_id: string
  client_secret?: string
  registration_access_token?: string
  registration_client_uri?: string
  client_id_issued_at?: number
  client_secret_expires_at?: number
  redirect_uris?: string[]
}

// Token response from OAuth server
export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token: string
  scope: string
}

// Connection state management
export interface ConnectionState {
  status: 'idle' | 'fetchedMetadata' | 'clientRegistered' | 'authorized' | 'connected' | 'error'
  serverUrl: string | null
  metadata: ServerMetadata | null
  clientRegistration: ClientRegistration | null
  tokenResponse: TokenResponse | null
  error: string | null
}

export type ServiceOAuthInitOptions = {
  serverUrl?: string
}

interface ConnectionOptions {
  serviceId?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

const initialConnectionState: ConnectionState = {
  status: 'idle',
  serverUrl: null,
  metadata: null,
  clientRegistration: null,
  tokenResponse: null,
  error: null,
}

export function useServiceOAuthConnection(options: ConnectionOptions) {
  const [connectionState, setConnectionState] = useState<ConnectionState>(initialConnectionState)
  const redirectUri = useServiceOAuthRedirectUri({ serviceId: options?.serviceId })

  const resetConnectionState = useCallback(() => {
    setConnectionState(initialConnectionState)
  }, [])

  // Store OAuth state in session storage for persistence across redirects
  const storeOAuthState = useCallback(
    (
      state: string,
      verifier: string,
      serverUrl: string,
      metadata: ServerMetadata,
      clientRegistration: ClientRegistration,
    ) => {
      sessionStorage.setItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_STATE, state)
      sessionStorage.setItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_VERIFIER, verifier)
      sessionStorage.setItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_SERVER_URL, serverUrl)
      sessionStorage.setItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_METADATA, JSON.stringify(metadata))
      sessionStorage.setItem(
        OAUTH_SESSION_STORAGE_KEYS.OAUTH_CLIENT_REGISTRATION,
        JSON.stringify(clientRegistration),
      )
    },
    [],
  )

  const getOAuthState = useCallback(() => {
    const state = sessionStorage.getItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_STATE)
    const verifier = sessionStorage.getItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_VERIFIER)
    const serverUrl = sessionStorage.getItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_SERVER_URL)
    const metadataJson = sessionStorage.getItem(OAUTH_SESSION_STORAGE_KEYS.OAUTH_METADATA)
    const clientRegistrationJson = sessionStorage.getItem(
      OAUTH_SESSION_STORAGE_KEYS.OAUTH_CLIENT_REGISTRATION,
    )

    return {
      state,
      verifier,
      serverUrl,
      metadata: metadataJson ? (JSON.parse(metadataJson) as ServerMetadata) : null,
      clientRegistration: clientRegistrationJson
        ? (JSON.parse(clientRegistrationJson) as ClientRegistration)
        : null,
    }
  }, [])

  const clearOAuthState = useCallback(() => {
    Object.values(OAUTH_SESSION_STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key)
    })
  }, [])

  // Discover OAuth server metadata
  const discoverMetadata = useMutation({
    mutationFn: async (serverUrl: string): Promise<ServerMetadata> => {
      const metadataUrl = new URL('/.well-known/oauth-authorization-server', serverUrl)
      const response = await fetch(metadataUrl.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw new Error(
          `Failed to discover server metadata: ${response.status} ${response.statusText}`,
        )
      }

      const metadata = await response.json()

      // Validate required endpoints
      if (
        !metadata.issuer ||
        !metadata.authorization_endpoint ||
        !metadata.token_endpoint ||
        !metadata.registration_endpoint
      ) {
        throw new Error('Invalid server metadata: missing required endpoints')
      }

      return metadata
    },
    onSuccess: (metadata, serverUrl) => {
      setConnectionState((prev) => ({
        ...prev,
        status: 'fetchedMetadata',
        serverUrl,
        metadata,
        error: null,
      }))
    },
  })

  // Dynamic client registration
  const registerClient = useMutation({
    mutationFn: async ({
      metadata,
      redirectUri,
      scopes,
      clientName,
    }: {
      metadata: ServerMetadata
      redirectUri: string
      scopes: string[]
      clientName?: string
    }): Promise<ClientRegistration> => {
      const registrationRequest = {
        client_name: clientName,
        client_uri: window.location.origin,
        redirect_uris: [redirectUri],
        grant_types: ['authorization_code'],
        response_types: ['code'],
        token_endpoint_auth_method: 'none', // PKCE
        scope: scopes.join(' '),
        application_type: 'web',
      }

      const response = await fetch(metadata.registration_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(registrationRequest),
      })

      if (!response.ok) {
        throw new Error(`Client registration failed: ${response.status} ${response.statusText}`)
      }

      const registration = await response.json()

      if (!registration.client_id) {
        throw new Error('Invalid client registration response: missing client_id')
      }

      return registration
    },
    onSuccess: (registration) => {
      setConnectionState((prev) => ({
        ...prev,
        status: 'clientRegistered',
        clientRegistration: registration,
        error: null,
      }))
    },
  })

  // Exchange authorization code for tokens
  const exchangeCodeForTokens = useMutation({
    mutationFn: async ({
      metadata,
      clientId,
      code,
      redirectUri,
      codeVerifier,
    }: {
      metadata: ServerMetadata
      clientId: string
      code: string
      redirectUri: string
      codeVerifier: string
    }): Promise<TokenResponse> => {
      const tokenRequest = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
      })

      const response = await fetch(metadata.token_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: tokenRequest,
      })

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`)
      }

      const tokens = await response.json()

      if (!tokens.access_token) {
        throw new Error('Invalid token response: missing access_token')
      }

      return tokens
    },
    onSuccess: (tokens) => {
      setConnectionState((prev) => ({
        ...prev,
        status: 'connected',
        tokenResponse: tokens,
        error: null,
      }))

      clearOAuthState()
      options?.onSuccess?.()
    },
    retry: false,
  })

  // Initiate OAuth connection flow
  const oauthInit = useCallback(
    async (oauthInitOptions: ServiceOAuthInitOptions) => {
      try {
        const serverUrl = oauthInitOptions?.serverUrl
        const scopes = ['profile', 'email']
        const clientName = 'Emergence CRAFT'

        if (!serverUrl) {
          throw new Error('Server URL is required')
        }

        // Discover server metadata
        const metadata = await discoverMetadata.mutateAsync(serverUrl)

        // Register client
        const registration = await registerClient.mutateAsync({
          metadata,
          redirectUri,
          scopes,
          clientName,
        })

        // Generate PKCE values
        const codeVerifier = generateCodeVerifier()
        const codeChallenge = await generateCodeChallenge(codeVerifier)
        const state = generateState()

        // Store OAuth state
        storeOAuthState(state, codeVerifier, serverUrl, metadata, registration)

        // Build authorization URL
        const registeredRedirectUri = registration.redirect_uris?.[0] || redirectUri
        const authUrl = new URL(metadata.authorization_endpoint)

        authUrl.searchParams.set('response_type', 'code')
        authUrl.searchParams.set('client_id', registration.client_id)
        authUrl.searchParams.set('redirect_uri', registeredRedirectUri)
        authUrl.searchParams.set('scope', scopes.join(' '))
        authUrl.searchParams.set('state', state)
        authUrl.searchParams.set('code_challenge', codeChallenge)
        authUrl.searchParams.set('code_challenge_method', 'S256')

        // Redirect to authorization server
        window.location.href = authUrl.toString()
      } catch (error) {
        console.error('Connection failed:', error)
        sessionStorage.removeItem(SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY)
        setConnectionState((prev) => ({
          ...prev,
          metadata: null,
          clientRegistration: null,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
        }))
        options?.onError?.(error instanceof Error ? error : new Error('Unknown error'))
      }
    },
    [options, discoverMetadata, registerClient, storeOAuthState],
  )

  // Handle OAuth callback after authorization
  const handleOAuthCallback = useCallback(
    async (code: string, state: string): Promise<TokenResponse | void> => {
      try {
        const oauthState = getOAuthState()

        if (
          !oauthState.state ||
          !oauthState.verifier ||
          !oauthState.serverUrl ||
          !oauthState.metadata ||
          !oauthState.clientRegistration
        ) {
          throw new Error('Missing OAuth state - please restart the connection process')
        }

        if (state !== oauthState.state) {
          throw new Error('Invalid OAuth state parameter')
        }

        // Update connection state with persisted data
        setConnectionState((prev) => ({
          ...prev,
          status: 'authorized',
          serverUrl: oauthState.serverUrl,
          metadata: oauthState.metadata,
          clientRegistration: oauthState.clientRegistration,
          error: null,
        }))

        const registeredRedirectUri =
          oauthState.clientRegistration.redirect_uris?.[0] || redirectUri

        // Exchange code for tokens
        return await exchangeCodeForTokens.mutateAsync({
          metadata: oauthState.metadata,
          clientId: oauthState.clientRegistration.client_id,
          code,
          redirectUri: registeredRedirectUri,
          codeVerifier: oauthState.verifier,
        })
      } catch (error) {
        console.error('OAuth callback handling failed:', error)
        clearOAuthState()
        setConnectionState((prev) => ({
          ...prev,
          metadata: null,
          clientRegistration: null,
          tokenResponse: null,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
        }))
        options?.onError?.(error instanceof Error ? error : new Error('Unknown error'))
      }
    },
    [],
  )

  return {
    oauthInit,
    isConnecting: ['fetchedMetadata', 'clientRegistered', 'authorized'].includes(
      connectionState.status,
    ),
    connectionState,
    handleOAuthCallback,
    clearOAuthState,
    resetConnectionState,
  }
}
